// Resolves the Supabase connection from environment variables.
//
// There is deliberately NO fallback value. A fallback to the production URL is
// the exact mechanism by which a dev build silently writes to real user data,
// so a missing variable must fail loudly at startup instead.
//
// Kept separate from client.ts so it can be unit-tested: client.ts touches
// localStorage at module load, which the node-environment test setup lacks.

export type SupabaseEnv = {
  url: string;
  publishableKey: string;
  /**
   * The project ref — the first label of the project host, derived from
   * `url`. (The production value is deliberately not spelled out here;
   * scripts/check-no-hardcoded-supabase-ref.mjs bans it from src/.) Callers
   * that build a function URL by hand need it; it is deliberately NOT a
   * separate environment variable, because a second copy of the same fact can
   * go stale against the first and silently address the wrong project.
   */
  projectRef: string;
};

// Assumption encoded here: VITE_SUPABASE_URL always holds a *hosted project*
// URL, whose host is exactly `<project-ref>.supabase.co`. That is what the
// Supabase dashboard hands you, and both `compaire-prod` and the dev project
// match it.
//
// Anything else throws rather than deriving a first label anyway. A custom
// domain is the case that matters: `auth.compaire.co` is a registered custom
// auth domain for this project, so a future URL of that shape would yield the
// ref "auth" and point social OAuth at https://auth.supabase.co — a real
// project owned by someone else, reached with no error. Local Supabase CLI
// stacks (http://127.0.0.1:54321) have no hosted ref at all. Guessing in
// either case reintroduces exactly the silent misroute this derivation
// exists to prevent, so the resolver keeps the file's fail-loudly doctrine.
function deriveProjectRef(url: string): string {
  let hostname: string;
  try {
    hostname = new URL(url).hostname;
  } catch {
    throw new Error(
      `VITE_SUPABASE_URL is not a valid URL: ${url}. Expected a Supabase ` +
        `project URL of the form https://<project-ref>.supabase.co.`,
    );
  }

  // Exactly three labels: `<ref>.supabase.co`. Matching the whole host rather
  // than a suffix is what rejects `supabase.co` (no ref) and any deeper
  // subdomain, instead of handing back a wrong-but-plausible ref.
  const [ref, ...rest] = hostname.split(".");
  const isProjectHost = ref.length > 0 && rest.join(".") === "supabase.co";

  if (!isProjectHost) {
    throw new Error(
      `VITE_SUPABASE_URL must be a Supabase project URL of the form ` +
        `https://<project-ref>.supabase.co, but got ${url}. The project ref ` +
        `is derived from this host, so a custom domain or a local stack ` +
        `cannot be used — deriving a ref from one would address the wrong ` +
        `project silently.`,
    );
  }

  return ref;
}

export function resolveSupabaseEnv(
  env: Record<string, string | undefined>,
): SupabaseEnv {
  // Trimmed because the URL is interpolated straight into a fetch path in
  // LinkPreview.tsx, where padding yields a malformed request — while
  // supabase-js trims internally and would look perfectly healthy.
  const url = env.VITE_SUPABASE_URL?.trim();
  const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();

  const missing: string[] = [];
  if (!url) missing.push("VITE_SUPABASE_URL");
  if (!publishableKey) missing.push("VITE_SUPABASE_PUBLISHABLE_KEY");

  if (missing.length > 0) {
    throw new Error(
      `Missing Supabase environment variable(s): ${missing.join(", ")}. ` +
        `There is no fallback by design — defaulting to production is how a dev ` +
        `build ends up writing to real user data. Copy .env.example to .env and ` +
        `set both values.`,
    );
  }

  // Derived from the already-trimmed, already-present URL, so the ref cannot
  // disagree with the connection the rest of the app uses.
  return { url, publishableKey, projectRef: deriveProjectRef(url) };
}
